import * as Blockly from 'blockly/core'
import { generateProjectCode } from '../codegen/index.js'

export function createProjectStore(workspace) {
  const state = {
    blocks: new Map(),
    version: 0,
    lastSaved: null,
    generatedCode: '',
    initialSprites: [],
    projectMeta: null,
  }
  const listeners = new Set()

  function emit(event) {
    listeners.forEach((fn) => fn(event))
  }

  workspace.addChangeListener((event) => {
    if (event.isUiEvent) return

    if (event.type === Blockly.Events.BLOCK_CREATE) {
      state.blocks.set(event.blockId, {
        type: event.json?.type || 'unknown',
        addedAt: Date.now(),
      })
    } else if (event.type === Blockly.Events.BLOCK_DELETE) {
      state.blocks.delete(event.blockId)
    } else if (
      event.type === Blockly.Events.BLOCK_MOVE ||
      event.type === Blockly.Events.BLOCK_CHANGE
    ) {
      const block = workspace.getBlockById(event.blockId)
      if (block) {
        const pos = block.getRelativeToSurfaceXY()
        state.blocks.set(event.blockId, { type: block.type, x: pos.x, y: pos.y })
      }
    }

    try {
      state.generatedCode = generateProjectCode(workspace, {
        initialSprites: state.initialSprites,
      })
    } catch (err) {
      state.generatedCode = `// Error: ${err.message}`
    }

    state.version++
    state.lastSaved = Date.now()
    listeners.forEach((fn) => fn(state))
  })

  return {
    getState: () => ({ ...state, blocks: new Map(state.blocks) }),
    subscribe: (fn) => {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    snapshot: () => ({
      version: state.version,
      blocks: Array.from(state.blocks.entries()).map(([id, b]) => ({ id, ...b })),
      generatedCode: state.generatedCode,
      lastSaved: state.lastSaved,
      initialSprites: [...state.initialSprites],
      projectMeta: state.projectMeta,
    }),
    getInitialSprites: () => [...state.initialSprites],
    setInitialSprites: (sprites) => {
      state.initialSprites = sprites
      try {
        state.generatedCode = generateProjectCode(workspace, {
          initialSprites: state.initialSprites,
        })
      } catch (err) {
        state.generatedCode = `// Error: ${err.message}`
      }
      emit({ type: 'initial_sprites_changed', sprites })
    },
    getProject: () => {
      const meta = state.projectMeta || {
        id: crypto.randomUUID?.() || `p-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: 'Proyek Tanpa Judul',
        templateId: null,
        createdAt: Date.now(),
      }
      return {
        ...meta,
        updatedAt: Date.now(),
        blocks: JSON.stringify(Blockly.serialization.workspaces.save(workspace)),
        canvas: { width: 720, height: 540, background: '#FFFFFF' },
        initialSprites: [...state.initialSprites],
        generatedCode: state.generatedCode,
        totalPlays: meta.totalPlays || 0,
        totalEdits: state.version,
        availableBlocks: meta.availableBlocks,
      }
    },
    loadFromProject: (project) => {
      workspace.clear()
      if (project.blocks) {
        try {
          const raw = typeof project.blocks === 'string' ? project.blocks.trim() : ''
          const inner = raw ? JSON.parse(raw) : project.blocks
          // Saved projects use the workspace save format from
          // Blockly.serialization.workspaces.save(), which already has the shape
          // expected by workspaces.load() (e.g. { blocks: { languageVersion, blocks: [...] } }).
          // Templates store only the blocks-serializer state ({ languageVersion, blocks: [...] }),
          // which must be wrapped so load() routes it to the blocks serializer.
          if (inner && typeof inner === 'object') {
            const stateToLoad = Array.isArray(inner.blocks) ? { blocks: inner } : inner
            Blockly.serialization.workspaces.load(stateToLoad, workspace)
          }
        } catch (err) {
          console.warn('[ProjectStore] Failed to parse blocks:', err)
        }
      }
      if (project.initialSprites) {
        state.initialSprites = project.initialSprites
      }
      state.projectMeta = {
        id: project.id,
        name: project.name,
        templateId: project.templateId,
        availableBlocks: project.availableBlocks,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
        totalPlays: project.totalPlays || 0,
      }
      try {
        state.generatedCode = generateProjectCode(workspace, {
          initialSprites: state.initialSprites,
        })
      } catch (err) {
        state.generatedCode = `// Error: ${err.message}`
      }
      emit({ type: 'project_loaded', project })
    },
    resetProject: () => {
      workspace.clear()
      state.initialSprites = []
      state.projectMeta = null
      state.generatedCode = ''
      emit({ type: 'project_reset' })
    },
  }
}
